import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Package, AlertTriangle, Calendar, Coins, Users, Phone, ScrollText, Search, RefreshCw, FileText, Download, Printer, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";

const peso = (n) => "₱" + Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtDT(iso) {
  try {
    return new Date(iso).toLocaleString("en-PH", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso || "—"); }
}
const MOVE_STYLE = {
  IN: "bg-green-50 text-green-700 border-green-200",
  OUT: "bg-red-50 text-red-700 border-red-200",
  ADJUST: "bg-amber-50 text-amber-700 border-amber-200",
  OPENING: "bg-blue-50 text-blue-700 border-blue-200",
};

const STOCK_STATUS = {
  ok: { label: "In Stock", cls: "bg-green-50 text-green-700 border-green-200" },
  low: { label: "Low", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  out: { label: "Out of Stock", cls: "bg-red-50 text-red-700 border-red-200" },
};

// Excel opens a bare .csv in the user's locale. Quote everything and prefix the
// BOM so peso values and medicine names with commas survive the round trip.
function toCsv(rows, columns) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.get(r))).join(",")).join("\n");
  return "﻿" + head + "\n" + body + "\n";
}

// Sortable column header. Tailwind can't see class names built by interpolation,
// so the alignment classes are spelled out rather than composed.
function SortTh({ k, sort, onSort, align = "left", children }) {
  const active = sort.key === k;
  return (
    <th className={`px-4 py-3 font-semibold ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 hover:text-slate-700 ${active ? "text-slate-900" : ""}`}
        data-testid={`report-sort-${k}`}
      >
        {children}
        <ArrowUpDown size={11} className={active ? "opacity-100" : "opacity-30"} />
      </button>
    </th>
  );
}

function downloadCsv(filename, csv) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function StatCard({ icon: Icon, label, value, sub, tone = "blue", testid }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    green: "bg-green-50 text-green-700",
  };
  return (
    <Card className="p-5 border-slate-200" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon size={18} /></div>
        <div className="min-w-0">
          <div className="text-2xl font-semibold text-slate-900 truncate" style={{ fontFamily: "Work Sans" }}>{value}</div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
          {sub ? <div className="text-xs text-slate-600 mt-1">{sub}</div> : null}
        </div>
      </div>
    </Card>
  );
}

export default function Inventory() {
  const [summary, setSummary] = useState(null);
  const [expiring, setExpiring] = useState({ count: 0, total_value_at_risk: 0, items: [] });
  const [windowMonths, setWindowMonths] = useState(6);
  const [patientsModal, setPatientsModal] = useState(null);

  // Stock-movement ledger (every IN / OUT / adjustment). Loaded lazily the first
  // time the Ledger tab is opened, then on filter changes.
  const [tab, setTab] = useState("expiring");
  const [ledger, setLedger] = useState(null);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [lq, setLq] = useState("");
  const [ltype, setLtype] = useState("all");
  const [lfrom, setLfrom] = useState("");
  const [lto, setLto] = useState("");

  // Full stock-on-hand report (every medicine, its price and its stock). Lazy
  // like the ledger — it reads the whole catalogue, so don't pay for it unless
  // the tab is actually opened.
  const [report, setReport] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [rq, setRq] = useState("");
  const [rcat, setRcat] = useState("all");
  const [rstatus, setRstatus] = useState("all");
  const [rsort, setRsort] = useState({ key: "medicine_name", dir: "asc" });

  const load = async () => {
    const [s, e] = await Promise.all([
      api.get("/inventory/summary"),
      api.get(`/inventory/expiring?months=${windowMonths}`),
    ]);
    setSummary(s.data);
    setExpiring(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowMonths]);

  const loadLedger = async () => {
    setLedgerBusy(true);
    try {
      const { data } = await api.get("/inventory/ledger", {
        params: { q: lq || undefined, movement_type: ltype, date_from: lfrom || undefined, date_to: lto || undefined },
      });
      setLedger(data);
    } catch {
      setLedger({ items: [], totals: { in: 0, out: 0, net: 0, rows: 0 } });
    } finally {
      setLedgerBusy(false);
    }
  };
  useEffect(() => {
    if (tab === "ledger") loadLedger();
    // eslint-disable-next-line
  }, [tab, ltype, lfrom, lto]);

  const loadReport = async () => {
    setReportBusy(true);
    try {
      const { data } = await api.get("/inventory/report", {
        params: { q: rq || undefined, category: rcat, status: rstatus },
      });
      setReport(data);
    } catch {
      setReport({ items: [], totals: { skus: 0, units: 0, value: 0, out_count: 0, low_count: 0 }, categories: [] });
    } finally {
      setReportBusy(false);
    }
  };
  useEffect(() => {
    if (tab === "report") loadReport();
    // eslint-disable-next-line
  }, [tab, rcat, rstatus]);

  // Sorting is client-side: the report is already fully in memory, and a round
  // trip per column click would be slower than the sort itself.
  const reportRows = useMemo(() => {
    const rows = [...(report?.items || [])];
    const { key, dir } = rsort;
    const mul = dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const x = a[key], y = b[key];
      if (typeof x === "number" && typeof y === "number") return (x - y) * mul;
      return String(x ?? "").localeCompare(String(y ?? ""), "en") * mul;
    });
    return rows;
  }, [report, rsort]);

  const sortBy = (key) =>
    setRsort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const exportReportCsv = () => {
    const cols = [
      { label: "Medicine", get: (r) => r.medicine_name },
      { label: "Generic", get: (r) => r.generic_name },
      { label: "Brand", get: (r) => r.brand_name },
      { label: "Strength", get: (r) => r.strength },
      { label: "Form", get: (r) => r.dosage_form },
      { label: "Category", get: (r) => r.category },
      { label: "Unit", get: (r) => r.unit },
      { label: "Unit Price (PHP)", get: (r) => Number(r.unit_price || 0).toFixed(2) },
      { label: "Stock Qty", get: (r) => r.stock_quantity },
      { label: "Stock Value (PHP)", get: (r) => Number(r.stock_value || 0).toFixed(2) },
      { label: "Reorder Level", get: (r) => r.low_stock_threshold },
      { label: "Status", get: (r) => STOCK_STATUS[r.status]?.label || r.status },
      { label: "Expiry", get: (r) => r.expiry_date },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`inventory-report-${stamp}.csv`, toCsv(reportRows, cols));
  };

  // Printing the live page would drag the app shell onto the paper, so render a
  // standalone document instead and print that.
  const printReport = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const t = report?.totals || {};
    const body = reportRows.map((r) => `<tr>
      <td>${esc(r.medicine_name)}${r.strength ? `<span class="sub"> · ${esc(r.strength)}</span>` : ""}</td>
      <td>${esc(r.category)}</td>
      <td class="r">${Number(r.unit_price || 0).toFixed(2)}</td>
      <td class="r">${r.stock_quantity}${r.unit ? ` ${esc(r.unit)}` : ""}</td>
      <td class="r">${Number(r.stock_value || 0).toFixed(2)}</td>
      <td>${esc(STOCK_STATUS[r.status]?.label || r.status)}</td>
      <td>${esc(r.expiry_date) || "—"}</td>
    </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Inventory Report</title>
      <style>
        body{font-family:system-ui,-apple-system,"Segoe UI",sans-serif;color:#0f172a;margin:24px}
        h1{font-size:18px;margin:0 0 2px}
        .meta{font-size:11px;color:#64748b;margin-bottom:14px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th,td{border-bottom:1px solid #e2e8f0;padding:6px 8px;text-align:left}
        th{background:#f8fafc;text-transform:uppercase;font-size:9px;letter-spacing:.05em;color:#64748b}
        .r{text-align:right;font-variant-numeric:tabular-nums}
        .sub{color:#94a3b8}
        tfoot td{font-weight:700;border-top:2px solid #0f172a;border-bottom:none}
        @page{margin:14mm}
      </style></head><body>
      <h1>Inventory Report — Stock on Hand</h1>
      <div class="meta">Generated ${new Date().toLocaleString("en-PH")} · ${t.skus || 0} SKUs · ${t.units || 0} units · Total value PHP ${Number(t.value || 0).toFixed(2)}</div>
      <table>
        <thead><tr><th>Medicine</th><th>Category</th><th class="r">Unit Price</th><th class="r">Stock</th><th class="r">Value</th><th>Status</th><th>Expiry</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="4">TOTAL</td><td class="r">${Number(t.value || 0).toFixed(2)}</td><td colspan="2"></td></tr></tfoot>
      </table></body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  if (!summary) return <div className="p-8 text-slate-500">Loading...</div>;

  const lowStock = Array.isArray(summary.low_stock) ? summary.low_stock : [];
  const byCat = Array.isArray(summary.by_category) ? summary.by_category : [];
  const items = Array.isArray(expiring.items) ? expiring.items : [];

  return (
    <div className="p-6 md:p-8 max-w-[1500px]" data-testid="inventory-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Inventory</h1>
        <p className="text-slate-500 text-sm mt-1">Stock value, expiring medicines, and patients who need them.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Coins} label="Total Inventory Value" value={peso(summary.total_value)} sub={`${summary.total_skus} SKUs · ${summary.total_items} units`} tone="blue" testid="stat-total-value" />
        <StatCard icon={AlertTriangle} label="Low Stock Items" value={summary.low_stock_count} sub="below threshold" tone="amber" testid="stat-low-stock" />
        <StatCard icon={Calendar} label={`Expiring within ${windowMonths} mo`} value={expiring.count} sub={`${peso(expiring.total_value_at_risk)} at risk`} tone="red" testid="stat-expiring" />
        <StatCard icon={Package} label="Active SKUs" value={summary.total_skus} sub={`${byCat.length} categories`} tone="green" testid="stat-skus" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="expiring" data-testid="tab-expiring">Expiring Soon</TabsTrigger>
          <TabsTrigger value="lowstock" data-testid="tab-lowstock">Low Stock</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">By Category</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger"><ScrollText size={14} className="mr-1" /> Ledger</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report"><FileText size={14} className="mr-1" /> Report</TabsTrigger>
        </TabsList>

        <TabsContent value="expiring">
          <Card className="border-slate-200">
            <div className="p-4 flex items-center gap-3 border-b border-slate-200 flex-wrap">
              <span className="text-sm text-slate-700 font-medium">Window:</span>
              {[3, 6, 9, 12].map((m) => (
                <Button
                  key={m}
                  size="sm"
                  variant={windowMonths === m ? "default" : "outline"}
                  className={windowMonths === m ? "bg-blue-600 hover:bg-blue-700" : ""}
                  onClick={() => setWindowMonths(m)}
                  data-testid={`window-${m}`}
                >
                  {m} months
                </Button>
              ))}
              <span className="ml-auto text-sm text-slate-500">Total value at risk: <b className="text-red-700">{peso(expiring.total_value_at_risk)}</b></span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Medicine</th>
                    <th className="px-4 py-3 text-left font-semibold">Expiry</th>
                    <th className="px-4 py-3 text-right font-semibold">Stock</th>
                    <th className="px-4 py-3 text-right font-semibold">Value at Risk</th>
                    <th className="px-4 py-3 text-left font-semibold">Patients to Notify</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No medicines expiring within {windowMonths} months. Nice!</td></tr>
                  ) : items.map((m) => (
                    <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`expiring-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{m.medicine_name}</div>
                        <div className="text-xs text-slate-500">{m.brand_name}{m.strength ? ` · ${m.strength}` : ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`text-xs font-medium ${m.expired ? "text-red-700" : m.days_left <= 30 ? "text-red-600" : m.days_left <= 90 ? "text-amber-600" : "text-slate-700"}`}>
                          {m.expiry_date}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                          {m.expired ? "EXPIRED" : `${m.days_left} days`}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right mono text-xs">{m.stock_quantity}{m.unit ? ` ${m.unit}` : ""}</td>
                      <td className="px-4 py-3 text-right mono text-xs font-semibold text-red-700">{peso(m.value_at_risk)}</td>
                      <td className="px-4 py-3">
                        {(m.patients_needing || []).length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setPatientsModal({ medicine: m.medicine_name, patients: m.patients_needing })} data-testid={`patients-btn-${m.id}`}>
                            <Users size={12} className="mr-1" /> {m.patients_needing.length} patient{m.patients_needing.length === 1 ? "" : "s"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="lowstock">
          <Card className="border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Medicine</th>
                    <th className="px-4 py-3 text-right font-semibold">Stock</th>
                    <th className="px-4 py-3 text-right font-semibold">Threshold</th>
                    <th className="px-4 py-3 text-right font-semibold">Unit Price</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">All medicines well stocked.</td></tr>
                  ) : lowStock.map((m) => (
                    <tr key={m.id} className="border-t border-slate-100" data-testid={`lowstock-row-${m.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{m.medicine_name}</td>
                      <td className="px-4 py-3 text-right mono text-xs text-red-700 font-semibold">{m.stock_quantity}{m.unit ? ` ${m.unit}` : ""}</td>
                      <td className="px-4 py-3 text-right mono text-xs">{m.threshold}</td>
                      <td className="px-4 py-3 text-right mono text-xs">{peso(m.unit_price)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button asChild size="sm" variant="outline"><Link to="/medicines">Edit</Link></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="categories">
          <Card className="border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Category</th>
                    <th className="px-4 py-3 text-right font-semibold">Items</th>
                    <th className="px-4 py-3 text-right font-semibold">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {byCat.length === 0 ? (
                    <tr><td colSpan="3" className="px-4 py-10 text-center text-slate-400">No data yet.</td></tr>
                  ) : byCat.map((c) => (
                    <tr key={c.category} className="border-t border-slate-100" data-testid={`cat-row-${c.category}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{c.category}</td>
                      <td className="px-4 py-3 text-right mono text-xs">{c.items}</td>
                      <td className="px-4 py-3 text-right mono text-xs font-semibold">{peso(c.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="ledger">
          <Card className="border-slate-200">
            {/* Filters */}
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-end gap-3">
              <form
                onSubmit={(e) => { e.preventDefault(); loadLedger(); }}
                className="relative flex-1 min-w-[200px]"
              >
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={lq} onChange={(e) => setLq(e.target.value)} placeholder="Search medicine, reference, or user…" className="pl-9" data-testid="ledger-search" />
              </form>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Type</div>
                <select value={ltype} onChange={(e) => setLtype(e.target.value)} className="h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" data-testid="ledger-type">
                  <option value="all">All movements</option>
                  <option value="IN">Stock In</option>
                  <option value="OUT">Stock Out</option>
                  <option value="ADJUST">Adjustments</option>
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">From</div>
                <Input type="date" value={lfrom} onChange={(e) => setLfrom(e.target.value)} className="w-[150px]" data-testid="ledger-from" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">To</div>
                <Input type="date" value={lto} onChange={(e) => setLto(e.target.value)} className="w-[150px]" data-testid="ledger-to" />
              </div>
              <Button variant="outline" onClick={loadLedger} disabled={ledgerBusy} data-testid="ledger-refresh">
                <RefreshCw size={14} className={`mr-1 ${ledgerBusy ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>

            {/* Totals */}
            {ledger?.totals ? (
              <div className="px-4 py-2 flex flex-wrap gap-4 text-xs border-b border-slate-100 bg-slate-50">
                <span className="text-green-700 font-semibold">In: +{ledger.totals.in}</span>
                <span className="text-red-700 font-semibold">Out: −{ledger.totals.out}</span>
                <span className="text-slate-700 font-semibold">Net: {ledger.totals.net >= 0 ? "+" : ""}{ledger.totals.net}</span>
                <span className="ml-auto text-slate-500">{ledger.totals.rows} movement{ledger.totals.rows === 1 ? "" : "s"}{ledger.limit && ledger.totals.rows >= ledger.limit ? ` (showing latest ${ledger.limit})` : ""}</span>
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Date / Time</th>
                    <th className="px-4 py-3 text-left font-semibold">Medicine</th>
                    <th className="px-4 py-3 text-center font-semibold">Type</th>
                    <th className="px-4 py-3 text-right font-semibold">In</th>
                    <th className="px-4 py-3 text-right font-semibold">Out</th>
                    <th className="px-4 py-3 text-right font-semibold">Balance</th>
                    <th className="px-4 py-3 text-left font-semibold">Source / Ref</th>
                    <th className="px-4 py-3 text-left font-semibold">By</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerBusy && !ledger ? (
                    <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
                  ) : (ledger?.items || []).length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400">No stock movements for this filter.</td></tr>
                  ) : ledger.items.map((r, i) => (
                    <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${i}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDT(r.datetime)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{r.medicine_name}</div>
                        <div className="text-xs text-slate-500">{r.brand_name}{r.strength ? ` · ${r.strength}` : ""}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-semibold ${MOVE_STYLE[r.movement_type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                          {r.movement_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right mono text-xs text-green-700 font-semibold">{r.qty_in ? `+${r.qty_in}` : ""}</td>
                      <td className="px-4 py-3 text-right mono text-xs text-red-700 font-semibold">{r.qty_out ? `−${r.qty_out}` : ""}</td>
                      <td className="px-4 py-3 text-right mono text-xs">{r.stock_after}{r.unit ? ` ${r.unit}` : ""}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        <div>{r.source || "—"}</div>
                        {r.reference ? <div className="text-[10px] text-slate-400 mono">{r.reference}</div> : null}
                        {r.reason || r.remarks ? <div className="text-[10px] text-slate-400">{r.reason || r.remarks}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{r.user_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="report">
          <Card className="border-slate-200" data-testid="report-card">
            {/* Filters + export */}
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-end gap-3">
              <form
                onSubmit={(e) => { e.preventDefault(); loadReport(); }}
                className="relative flex-1 min-w-[200px]"
              >
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={rq} onChange={(e) => setRq(e.target.value)} placeholder="Search medicine, generic, or brand…" className="pl-9" data-testid="report-search" />
              </form>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Category</div>
                <select value={rcat} onChange={(e) => setRcat(e.target.value)} className="h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" data-testid="report-category">
                  <option value="all">All categories</option>
                  {(report?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Status</div>
                <select value={rstatus} onChange={(e) => setRstatus(e.target.value)} className="h-10 border border-slate-200 rounded-md px-3 text-sm bg-white" data-testid="report-status">
                  <option value="all">All stock</option>
                  <option value="ok">In stock</option>
                  <option value="low">Low stock</option>
                  <option value="out">Out of stock</option>
                </select>
              </div>
              <Button variant="outline" onClick={loadReport} disabled={reportBusy} data-testid="report-refresh">
                <RefreshCw size={14} className={`mr-1 ${reportBusy ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button variant="outline" onClick={exportReportCsv} disabled={!reportRows.length} data-testid="report-export">
                <Download size={14} className="mr-1" /> Export CSV
              </Button>
              <Button variant="outline" onClick={printReport} disabled={!reportRows.length} data-testid="report-print">
                <Printer size={14} className="mr-1" /> Print
              </Button>
            </div>

            {/* Totals strip */}
            {report?.totals ? (
              <div className="px-4 py-2 flex flex-wrap gap-4 text-xs border-b border-slate-100 bg-slate-50" data-testid="report-totals">
                <span className="text-slate-700 font-semibold">{report.totals.skus} SKU{report.totals.skus === 1 ? "" : "s"}</span>
                <span className="text-slate-700">{report.totals.units} units on hand</span>
                <span className="text-blue-700 font-semibold">Total value: {peso(report.totals.value)}</span>
                {report.totals.low_count ? <span className="text-amber-700 font-semibold">{report.totals.low_count} low</span> : null}
                {report.totals.out_count ? <span className="text-red-700 font-semibold">{report.totals.out_count} out of stock</span> : null}
                {report.totals.expired_count ? <span className="text-red-700 font-semibold">{report.totals.expired_count} expired</span> : null}
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <SortTh k="medicine_name" sort={rsort} onSort={sortBy}>Medicine</SortTh>
                    <SortTh k="category" sort={rsort} onSort={sortBy}>Category</SortTh>
                    <SortTh k="unit_price" sort={rsort} onSort={sortBy} align="right">Unit Price</SortTh>
                    <SortTh k="stock_quantity" sort={rsort} onSort={sortBy} align="right">Stock</SortTh>
                    <SortTh k="stock_value" sort={rsort} onSort={sortBy} align="right">Stock Value</SortTh>
                    <SortTh k="status" sort={rsort} onSort={sortBy}>Status</SortTh>
                    <SortTh k="expiry_date" sort={rsort} onSort={sortBy}>Expiry</SortTh>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {reportBusy && !report ? (
                    <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
                  ) : reportRows.length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400">No medicines match this filter.</td></tr>
                  ) : reportRows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`report-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{r.medicine_name}</div>
                        <div className="text-xs text-slate-500">
                          {[r.brand_name, r.strength, r.dosage_form].filter(Boolean).join(" · ") || r.generic_name || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{r.category}</td>
                      <td className="px-4 py-3 text-right mono text-xs">{peso(r.unit_price)}</td>
                      <td className="px-4 py-3 text-right mono text-xs">
                        <span className={r.status === "out" ? "text-red-700 font-semibold" : r.status === "low" ? "text-amber-700 font-semibold" : ""}>
                          {r.stock_quantity}{r.unit ? ` ${r.unit}` : ""}
                        </span>
                        {r.tracked ? null : <div className="text-[10px] text-slate-400">not stocked here</div>}
                      </td>
                      <td className="px-4 py-3 text-right mono text-xs font-semibold">{peso(r.stock_value)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-semibold ${STOCK_STATUS[r.status]?.cls || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                          {STOCK_STATUS[r.status]?.label || r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {r.expiry_date ? (
                          <>
                            <div className={r.expired ? "text-red-700 font-medium" : r.days_left != null && r.days_left <= 90 ? "text-amber-600" : "text-slate-600"}>
                              {r.expiry_date}
                            </div>
                            {r.days_left != null ? (
                              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                                {r.expired ? "EXPIRED" : `${r.days_left} days`}
                              </div>
                            ) : null}
                          </>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button asChild size="sm" variant="outline"><Link to="/medicines">Edit</Link></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {reportRows.length ? (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                      <td className="px-4 py-3 text-slate-900" colSpan="3">Total ({reportRows.length} SKUs)</td>
                      <td className="px-4 py-3 text-right mono text-xs">{reportRows.reduce((s, r) => s + Number(r.stock_quantity || 0), 0)}</td>
                      <td className="px-4 py-3 text-right mono text-xs text-blue-700">{peso(reportRows.reduce((s, r) => s + Number(r.stock_value || 0), 0))}</td>
                      <td colSpan="3"></td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!patientsModal} onOpenChange={(o) => !o && setPatientsModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="patients-modal">
          <DialogHeader>
            <DialogTitle>Patients prescribed: {patientsModal?.medicine}</DialogTitle>
          </DialogHeader>
          <div className="divide-y divide-slate-200 max-h-96 overflow-auto">
            {(patientsModal?.patients || []).map((p) => (
              <div key={p.patient_id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">{p.patient_name || "(unknown)"}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                    {p.contact_number ? <span className="flex items-center gap-1"><Phone size={11} /> <span className="mono">{p.contact_number}</span></span> : null}
                    {p.last_prescribed ? <span>Last: {String(p.last_prescribed).slice(0, 10)}</span> : null}
                    {p.rx_number ? <span className="mono">{p.rx_number}</span> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
